import React, { type ReactNode, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';

import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import { Input } from 'src/shared/components/ui/input';

type TypeToConfirmDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** What kind of thing this is — "organisation", "project" — read above its name. */
    entityLabel: string;
    /** The name, which leads the dialog and must be typed back verbatim before the action unlocks. */
    confirmationText: string;
    /** Names what has to be typed, e.g. "Type the organisation name to confirm". */
    confirmationPrompt: string;
    /** What is about to happen, in plain words. */
    warning: ReactNode;
    /** What the action would take with it. */
    blastRadius: ReactNode;
    confirmLabel: string;
    onConfirm: () => void;
    isConfirming: boolean;
    errorMessage?: string | null;
    /** Given only once the act has succeeded. Its presence is what puts the dialog in its done state. */
    receipt?: ReactNode;
};

const DestructiveIcon = () => (
    <span
        className='mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-0'
        data-testid='type-to-confirm-icon'
        aria-hidden='true'
    >
        <Trash2 size={22} className='text-red-100' />
    </span>
);

/**
 * A confirmation for something irreversible. Typing the name is deliberate friction: the actions
 * behind this cascade across stores with no rollback, so the cost of a misplaced click is a restore
 * from backup. The gate resets whenever the dialog closes, so a second attempt starts shut.
 *
 * The name is the heading and the figures are a footnote to it. What an operator must get right here
 * is *which* thing they are deleting; the counts only tell them how much it would cost.
 */
const TypeToConfirmDialog = ({
    open,
    onOpenChange,
    entityLabel,
    confirmationText,
    confirmationPrompt,
    warning,
    blastRadius,
    confirmLabel,
    onConfirm,
    isConfirming,
    errorMessage,
    receipt,
}: TypeToConfirmDialogProps) => {
    const [typed, setTyped] = useState('');
    const inputId = 'type-to-confirm-input';

    useEffect(() => {
        if (!open) {
            setTyped('');
        }
    }, [open]);

    const heading = (
        <span className='block text-center'>
            <span className='block text-xs font-medium uppercase tracking-wide text-grey-70'>Delete {entityLabel}</span>
            <span className='mt-1 block break-words text-2xl leading-tight text-black'>{confirmationText}</span>
        </span>
    );

    if (receipt) {
        return (
            <ControlledConfirmDialog
                open={open}
                onOpenChange={onOpenChange}
                variant='alert'
                icon={<DestructiveIcon />}
                title={heading}
                confirmLabel='Close'
                onConfirm={() => onOpenChange(false)}
            >
                <div className='flex flex-col gap-2 text-center text-sm text-grey-70'>{receipt}</div>
            </ControlledConfirmDialog>
        );
    }

    return (
        <ControlledConfirmDialog
            open={open}
            onOpenChange={onOpenChange}
            variant='confirm'
            icon={<DestructiveIcon />}
            title={heading}
            description={<span className='block text-center'>{warning}</span>}
            confirmLabel={confirmLabel}
            confirmDisabled={typed !== confirmationText}
            onConfirm={onConfirm}
            isConfirming={isConfirming}
        >
            <div className='flex flex-col gap-4 text-left'>
                {blastRadius}

                <div className='flex flex-col gap-1'>
                    <label htmlFor={inputId} className='text-sm text-grey-70'>
                        {confirmationPrompt}
                    </label>
                    <Input
                        id={inputId}
                        value={typed}
                        onChange={event => setTyped(event.target.value)}
                        data-testid='type-to-confirm-input'
                        autoComplete='off'
                    />
                </div>

                {errorMessage && (
                    <span role='alert' className='text-sm text-red-100'>
                        {errorMessage}
                    </span>
                )}
            </div>
        </ControlledConfirmDialog>
    );
};

export default TypeToConfirmDialog;
